import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { updateUserProfile } from '../../services/api';
import ChangePasswordForm from './ChangePasswordForm';
import DeleteAccountSection from './DeleteAccountSection';
import ReferralSection from './ReferralSection';

function ProfilePage() {
  const navigate = useNavigate();
  const { user, canChangePassword, tierDisplay, tier, hasSubscription, hasAffiliateProfile, loading, deletionPending, deletionPermanentAt, deletionScheduledAt, showUsername, refreshProfile } = useUser();
  const [savingShowUsername, setSavingShowUsername] = useState(false);

  const handleShowUsernameToggle = async () => {
    setSavingShowUsername(true);
    try {
      await updateUserProfile({ show_username: !showUsername });
      await refreshProfile();
    } catch (err) {
      console.error('Failed to update show_username setting:', err);
    } finally {
      setSavingShowUsername(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted">Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted">Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8">
      <h1 className="text-2xl font-bold text-brand-white mb-8">Account Settings</h1>

      {/* Deletion Countdown Banner */}
      {deletionPending && (
        <div style={{
          padding: '16px 20px',
          marginBottom: '24px',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ color: '#ef4444', fontSize: '20px', fontWeight: 'bold' }}>!</span>
            <h3 style={{ color: '#ef4444', fontSize: '16px', fontWeight: '600', margin: 0 }}>
              Account Deletion Pending
            </h3>
          </div>
          <p style={{ color: '#fca5a5', fontSize: '14px', margin: '0 0 8px 0', lineHeight: '1.5' }}>
            Your account is scheduled for deletion. Your data will be permanently removed in{' '}
            <strong style={{ color: '#fff' }}>
              {Math.max(0, Math.ceil((new Date(deletionPermanentAt) - new Date()) / (1000 * 60 * 60 * 24)))} days
            </strong>
            {' '}(on {new Date(deletionPermanentAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}).
          </p>
          <p style={{ color: '#d1d5db', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
            If you did not mean to do this, please cancel the deletion below or contact support at{' '}
            <a href="mailto:daniel@foobalabs.com" style={{ color: '#218aff', textDecoration: 'underline' }}>
              daniel@foobalabs.com
            </a>
          </p>
        </div>
      )}

      {/* Account Information Section */}
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Account Information</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-muted mb-1">Username</label>
            <p className="text-brand-white">{user.username}</p>
          </div>

          <div>
            <label className="block text-sm text-muted mb-1">Email</label>
            <p className="text-brand-white">{user.email}</p>
          </div>

          <div>
            <label className="block text-sm text-muted mb-1">Display Username in Navbar</label>
            <div className="flex items-center gap-3">
              <button
                onClick={handleShowUsernameToggle}
                disabled={savingShowUsername}
                style={{
                  position: 'relative',
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  backgroundColor: showUsername ? '#218aff' : '#444',
                  border: 'none',
                  cursor: savingShowUsername ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.3s ease',
                  opacity: savingShowUsername ? 0.5 : 1,
                  padding: 0,
                  flexShrink: 0,
                }}
                aria-label="Toggle username display in navbar"
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '3px',
                    left: showUsername ? '23px' : '3px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    transition: 'left 0.3s ease',
                  }}
                />
              </button>
              <span className="text-sm text-muted">
                {showUsername ? 'Username visible in navbar' : 'Username hidden in navbar (shows "Account")'}
              </span>
            </div>
            <p className="text-xs mt-1" style={{ color: '#666' }}>
              Hide your username when screen sharing. Your username is always visible here in Account Settings.
            </p>
          </div>

          <div>
            <label className="block text-sm text-muted mb-1">Subscription</label>
            <div className="flex items-center gap-4">
              <p className="text-brand-white">{tierDisplay || 'Free'}</p>
              {tier === 'free' || !tier ? (
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-3 py-1 bg-brand-blue text-white text-sm rounded hover:bg-brand-blue/80 transition-colors"
                >
                  Upgrade
                </button>
              ) : (
                <button
                  onClick={() => navigate('/subscription')}
                  className="px-3 py-1 border border-brand-blue text-brand-blue text-sm rounded hover:bg-brand-blue/10 transition-colors"
                >
                  Manage Subscription
                </button>
              )}
            </div>
          </div>

          {user.is_staff && (
            <div>
              <label className="block text-sm text-muted mb-1">Role</label>
              <span className="inline-block px-2 py-1 bg-brand-blue/20 text-brand-blue text-sm rounded">
                Administrator
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Password Section */}
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Password</h2>

        {canChangePassword ? (
          <ChangePasswordForm />
        ) : (
          <div className="bg-gray-800/50 rounded p-4">
            <p className="text-muted text-sm">
              Your account uses Google authentication. Password management is handled by Google.
            </p>
          </div>
        )}
      </section>

      {/* Affiliate Program Link */}
      {hasAffiliateProfile && (
        <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
          <h2 className="text-lg font-semibold text-brand-white mb-2">Affiliate Program</h2>
          <p className="text-muted text-sm mb-3">Manage your campaign codes, track referrals, and view earnings.</p>
          <Link
            to="/affiliate"
            className="inline-block px-4 py-2 bg-brand-blue text-white text-sm rounded hover:bg-brand-blue/80 transition-colors"
          >
            Go to Affiliate Dashboard
          </Link>
        </section>
      )}

      {/* Referral Attribution Section */}
      <ReferralSection />

      {/* Delete Account Section (not shown for admin users) */}
      {!user.is_staff && <DeleteAccountSection />}
    </div>
  );
}

export default ProfilePage;
